import * as Sentry from '@sentry/react';

// Initialize Sentry only in production
export function initSentry() {
  if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.MODE,

      // Performance monitoring
      tracesSampleRate: 0.1, // 10% of transactions

      // Session replay for error debugging
      replaysSessionSampleRate: 0.01, // 1% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

      // Filter out known non-issues
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        // Network errors
        'Network Error',
        'Failed to fetch',
        'Load failed',
        // Cancel errors
        'AbortError',
        'CanceledError',
      ],

      // Don't send errors from localhost
      beforeSend(event) {
        if (window.location.hostname === 'localhost') {
          return null;
        }
        return event;
      },

      // Integrations
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Mask all text and inputs for privacy
          maskAllText: false,
          maskAllInputs: true,
          // Block media elements to reduce payload
          blockAllMedia: true,
        }),
      ],
    });

    console.log('[Sentry] Initialized');
  } else {
    console.log('[Sentry] Skipped initialization (dev mode or no DSN)');
  }
}

// Capture exception with additional context
export function captureException(
  error: unknown,
  context?: Record<string, unknown>
) {
  if (import.meta.env.PROD) {
    Sentry.captureException(error, {
      extra: context,
    });
  } else {
    console.error('[Sentry] Would capture:', error, context);
  }
}

// Capture message
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info'
) {
  if (import.meta.env.PROD) {
    Sentry.captureMessage(message, level);
  } else {
    console.log(`[Sentry] Would capture message (${level}):`, message);
  }
}

// Set user context
export function setUser(user: { id: string; email?: string; name?: string } | null) {
  if (import.meta.env.PROD) {
    Sentry.setUser(user);
  }
}

// Add breadcrumb for debugging
export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>
) {
  if (import.meta.env.PROD) {
    Sentry.addBreadcrumb({
      category,
      message,
      data,
      level: 'info',
    });
  }
}

// Start a transaction for performance monitoring
export function startTransaction(name: string, op: string) {
  if (import.meta.env.PROD) {
    return Sentry.startInactiveSpan({ name, op });
  }
  return null;
}

// Export Sentry for direct access if needed
export { Sentry };
